import { Button, Chip, Container, Divider, Grid, Paper, Typography } from "@mui/material";
import UploadIcon from '@mui/icons-material/Publish';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useContext, useEffect, useState } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import { PAGE_SIZE } from "../constants/Constants";
import { format } from "date-fns";
import UploadDialog from "../sections/ats-fulfillment/UploadDialog";
import { AuthContext } from "../context/AuthProvider";
import moment from "moment";
import DetailViewDialog from "../sections/ats-fulfillment/DetailViewDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const DEFAULT_FILTER = {
  date: null,
};

const STATUS = {
  pending: "Pending",
  processing: "In Progress",
  completed: "Completed",
  failed: "Failed",
}

const STATUS_COLOR = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
}

export default function FulfillmentAndTracking() {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const authContext = useContext(AuthContext);

  const handleReset = () => setFilter(DEFAULT_FILTER);

  const goToDetailView = (id) => {
    // Navigate to detail view page
    setSelectedItemId(id);
    setDetailDialogVisible(true);
  }
  const closeDetailView = () => {
    setSelectedItemId(null);
    setDetailDialogVisible(false);
    loadData(true);
  }

  const loadData = async (resetPage) => {
    setIsLoading(true);

    let filters = {
      limit: PAGE_SIZE,
      offset: resetPage ? 0 : paginationModel.page * PAGE_SIZE,
    };
    if (filter.date) {
      filters.dateFrom = moment(filter.date).startOf('day').format();
      filters.dateTo = moment(filter.date).endOf('day').format();
    }
    const params = new URLSearchParams(filters);

    try {
      const response = await fetchWithInterceptor(
        `/api/ats-fulfillment/list?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      const data = await response.json();
      setRows(data.items);
      setRowCount(data.total);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadData(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadData(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const showDialog = () => setDialogVisible(true);
  const hideDialog = (reload, fileId) => {
    setDialogVisible(false);
    if (reload) {
      loadData(true);
    }
    if (fileId) {
      goToDetailView(fileId);
    }
  }

  const columns = [
    {
      field: "createdAt",
      headerName: "Uploaded at",
      flex: 1,
      sortable: false,
      valueGetter: (value) => value
          ? format(new Date(value), "yyyy-MM-dd hh:mm a")
          : ""
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={STATUS[params.value]}
            size="small"
            variant="filled"
            color={STATUS_COLOR[params.value]} 
          />
        );
      }
    },
    {
      field: "id",
      headerName: "Detail",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Button color="primary" onClick={() => goToDetailView(params?.row?.id, params?.row?.status)}>View details</Button>
        );
      },
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Upload the ATS CSV file to fulfill Shopify orders and automatically attach tracking numbers. Once processed, customers will receive a notification email with their shipment details.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Uploaded at"
                  value={filter.date}
                  onChange={(date) => setFilter({ ...filter, date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: "auto" }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleReset}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid container justifyContent="flex-end" style={{ padding: 16 }} spacing={1}>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={showDialog}
                startIcon={<UploadIcon />}
              >
                Upload CSV
              </Button>
            </Grid>
          </Grid>

          <CustomDataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            loading={isLoading}
          />
        </Paper>
      </Container>
      <UploadDialog open={dialogVisible} onClose={hideDialog} />
      <DetailViewDialog open={detailDialogVisible} onClose={closeDetailView} itemId={selectedItemId} />
    </>
  );
}